import { generateAccessName, AccessKey } from "../../../../../access";

/**
 * Base keys of the access sections on this page.
 */
const ACCESS_SECTION: AccessKey[] = [
	AccessKey.SETTINGS,
	AccessKey.SETTINGS_FINANCES,
];

/**
 * Enum representing special keys for each tab on this page.
 * These keys are used to uniquely identify and manage individual tabs.
 */
enum TabKeys {
	EXECUTOR_TARIFF_PLANS = "executorTariffPlans",
	CUSTOMER_BALANCES = "customerBalances",
	CUSTOMER_TARIFF_PLANS = "customerTariffPlans",
	USER_TARIFF_PLANS = "userTariffPlans",
	EXECUTOR_PENALTIES = "executorPenalties",
	CUSTOMER_PENALTIES = "customerPenalties",
	USER_PENALTIES = "userPenalties",
}

type TabAccessNamesType = {
	[key in TabKeys]: string;
};

/**
 * Constructs an array of access addresses by combining base access addresses
 * with the tab-specific access keys. This builds the full set of addresses
 * needed to manage access from the page level to the specific tab.
 *
 * @param key - Tab-specific access keys.
 * @returns Array of access addresses including base and tab-specific addresses.
 */
const generateAccessSection = (key: AccessKey): AccessKey[] => [
	...ACCESS_SECTION,
	key,
];

/**
 * Maps each tab on the page to its corresponding array of access addresses.
 * This container is used for managing access control not only for the tabs themselves,
 * but also for internal elements such as buttons within these tabs.
 * Each entry provides a set of access addresses specific to the tab.
 */
const ARR_ACCESS_PART_KEY: Record<TabKeys, AccessKey[]> = {
	[TabKeys.EXECUTOR_TARIFF_PLANS]: generateAccessSection(
		AccessKey.EXECUTOR_TARIFF_PLANS,
	),
	[TabKeys.CUSTOMER_BALANCES]: generateAccessSection(
		AccessKey.CUSTOMER_BALANCES,
	),
	[TabKeys.CUSTOMER_TARIFF_PLANS]: generateAccessSection(
		AccessKey.CUSTOMER_TARIFF_PLANS,
	),
	[TabKeys.USER_TARIFF_PLANS]: generateAccessSection(
		AccessKey.USER_TARIFF_PLANS,
	),
	[TabKeys.EXECUTOR_PENALTIES]: generateAccessSection(
		AccessKey.EXECUTOR_PENALTIES,
	),
	[TabKeys.CUSTOMER_PENALTIES]: generateAccessSection(
		AccessKey.CUSTOMER_PENALTIES,
	),
	[TabKeys.USER_PENALTIES]: generateAccessSection(AccessKey.USER_PENALTIES),
};

/**
 * Generates access names from arrays of access keys for each tab.
 * This mapping creates a unique access name for each tab, which is used
 * to determine whether the tab is accessible or not. Each access name represents
 * the combined access keys for the tab.
 *
 * @returns TabAccessNamesType - An object mapping each tab key to its unique access name
 */
const TabAccessNames: TabAccessNamesType = Object.fromEntries(
	Object.entries(ARR_ACCESS_PART_KEY).map(([key, section]) => [
		key,
		generateAccessName(...section),
	]),
) as TabAccessNamesType;

const mainPath = `settings.finances`;

export { TabKeys, TabAccessNames, ARR_ACCESS_PART_KEY, mainPath };
